import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { useReservedHours } from '@/hooks/useReservedHours';
import { Loader2, Users } from 'lucide-react';
import { useState } from 'react';

import { useReservationForm } from '@/hooks/useReservationForm';
import { submitReservation } from '@/lib/submitReservation';
import AlertError from './alert-error';
import { ReservationFormFields } from './ReservationFormFields';
import { ReservationSuccess } from './ReservationSuccess';
import { ReservationSummary } from './ReservationSummary';

interface Props {
    room: App.Data.RoomData | App.Data.TableData;
    reservableType: 'room' | 'table';
    customer: App.Data.CustomerData | null;
    isOpen: boolean;
    onClose: () => void;
}

export function ReservationModal({
    room,
    reservableType,
    customer,
    isOpen,
    onClose,
}: Props) {
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const { data, setData, errors, processing, reset } = useReservationForm({
        customer,
        reservableType,
        reservableId: room.id,
    });

    const { reservedHours, loading: loadingHours } = useReservedHours(
        reservableType,
        room?.id ?? null,
        isOpen && !!room ? (data.date ? new Date(data.date) : null) : null,
    );

    if (!room) return null;

    return (
        <Dialog
            open={isOpen}
            onOpenChange={(open) => {
                if (!open) {
                    reset();
                    onClose();
                }
            }}
        >
            <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[480px]">
                {success ? (
                    // SUCCESS SCREEN
                    <ReservationSuccess />
                ) : (
                    <>
                        <DialogHeader>
                            <DialogTitle>Reserve {room.name}</DialogTitle>
                            <DialogDescription>
                                Fill your details to confirm a reservation.
                            </DialogDescription>
                        </DialogHeader>

                        {/* ROOM IMAGE */}
                        <div className="mt-4 flex items-center gap-3">
                            <div className="h-20 w-28 overflow-hidden rounded-md">
                                <img
                                    src={room.image}
                                    className="h-full w-full object-cover"
                                />
                            </div>
                            <div className="grid gap-1">
                                <p className="font-medium">{room.name}</p>
                                <p className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <Users className="h-4 w-4" />
                                    {room.capacity} people
                                </p>
                            </div>
                        </div>

                        {/* FORM */}
                        <form
                            onSubmit={(e) => {
                                e.preventDefault();

                                submitReservation({
                                    data,
                                    onSuccess: () => {
                                        setSuccess(true);
                                        setTimeout(() => {
                                            onClose();
                                            reset();
                                            setSuccess(false);
                                        }, 1800);
                                    },
                                    onError: setError,
                                });
                            }}
                            className="mt-6 space-y-6"
                        >
                            {/* FORM FIELDS */}
                            <ReservationFormFields
                                data={data}
                                setData={setData}
                                errors={errors}
                                room={room}
                                reservedHours={reservedHours}
                                loadingHours={loadingHours}
                            />
                            {/* Confirmation summary */}
                            {data.start_time && data.end_time && (
                                <ReservationSummary data={data} />
                            )}
                            {/* ERROR ALERT */}
                            {error && (
                                <AlertError
                                    errors={[error]}
                                    title="Something went wrong."
                                />
                            )}
                            {/* BUTTON */}
                            <Button
                                type="submit"
                                disabled={
                                    !data.name ||
                                    !data.phone ||
                                    !data.date ||
                                    !data.start_time ||
                                    !data.end_time ||
                                    data.guests < 1 ||
                                    data.guests > 10 ||
                                    processing
                                }
                                className="w-full"
                            >
                                {processing ? (
                                    <div className="flex justify-center py-3">
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    </div>
                                ) : (
                                    'Confirm Reservation'
                                )}
                            </Button>
                        </form>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
